// View-only workout list item for the calendar sidebar "phone" preview.
// Modeled on the workouts module's StandardWorkoutListItem, but copied into the
// calendar module so the two never share components.
import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';

import iconSet from '../../../../shared/images/teambuildr-selection.json';

const WorkoutItemDiv = styled('div')`
  display: flex;
  cursor: pointer;
  overflow: hidden;
  min-height: 55px;
  padding: 0 10px;
  :hover {
    opacity: 0.7;
  }
`;

const IconTitle = styled('div')`
  display: flex;
  max-width: 100%;
  width: 100%;
  overflow: hidden;
`;

const TitleDesc = styled('div')`
  max-width: 100%;
  width: 100%;
  white-space: nowrap;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 15px;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SubTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 1.3px;
  color: #9e9e9e;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const IconContainer = styled('div')`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: ${(props) => (!props.completed ? `1px solid ${props.borderColor}` : '#0dcc8a')};
  width: 30px;
  height: 30px;
  min-width: 30px;
  background: ${(props) => (props.completed ? '#0dcc8a' : 'white')};
`;

const IconAndLine = styled('div')`
  display: flex;
  flex-direction: column;
  margin-right: 16px;
  align-items: center;
`;

const GroupingLetterContainer = styled('div')`
  display: flex;
  align-items: center;
  border-radius: 50px;
  border-width: 1px;
  height: 15px;
  justify-content: center;
  padding: 1.5px;
  position: absolute;
  width: 15px;
  border: ${(props) => (!props.completed ? `1px solid ${props.borderColor}` : '#0dcc8a')};
  background: ${(props) => props.borderColor};
  right: -3px;
  bottom: -3px;
`;

const GroupingLetter = styled('div')`
  color: ${(props) => props.color};
  font-size: 9px;
  font-weight: 500;
`;

const CalendarWorkoutListItem = ({ workoutItem, showVerticalLine, onSelect }) => {
  const theme = useTheme();

  return (
    <WorkoutItemDiv onClick={onSelect}>
      <IconAndLine>
        <IconContainer
          borderColor={workoutItem?.groupingColorCode || 'grey'}
          completed={workoutItem?.fullyCompleted}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={workoutItem.fullyCompleted ? 'checkmark' : workoutItem.icon}
            color={workoutItem.fullyCompleted ? 'white' : 'black'}
          />
          {workoutItem.groupingColorCode && workoutItem.groupingLetter && (
            <GroupingLetterContainer
              borderColor={workoutItem?.groupingColorCode || 'black'}
              completed={workoutItem?.fullyCompleted}
            >
              <GroupingLetter color={theme.colors.background[0]}>
                {workoutItem.groupingLetter}
              </GroupingLetter>
            </GroupingLetterContainer>
          )}
        </IconContainer>
        <div
          style={{
            height: '25px',
            width: showVerticalLine ? '2px' : '0px',
            background: 'lightgrey',
          }}
        />
      </IconAndLine>
      <IconTitle>
        <TitleDesc>
          <Title>{workoutItem.title}</Title>
          {workoutItem?.subTitle ? (
            <SubTitle>{workoutItem.subTitle.toUpperCase()}</SubTitle>
          ) : null}
        </TitleDesc>
      </IconTitle>
    </WorkoutItemDiv>
  );
};

CalendarWorkoutListItem.defaultProps = {
  showVerticalLine: false,
};

CalendarWorkoutListItem.propTypes = {
  workoutItem: PropTypes.instanceOf(Object).isRequired,
  showVerticalLine: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

export default CalendarWorkoutListItem;
